import React from "react";
import { DayPicker } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { S3 } from "../../constants";
import { useRillContext } from "../../rill/RillReact/share/base";
import {
  timezoneOptionLabel,
  timezoneOptionsWith,
} from "../../utils/scheduling-timezones";
import BookTourConfirmation from "./BookTourConfirmation";
import BookTourForm from "./BookTourForm";
import BookTourSelect from "./BookTourSelect";
import {
  BookTourError,
  createTourBooking,
  fetchTourSlots,
} from "./book-tour-api";
import {
  dateFromYmd,
  formatCalendarDay,
  formatLongDate,
  formatSlotTime,
  gymToday,
  horizonEnd,
  ymd,
  zoneLabel,
} from "./book-tour-format";

/**
 * The public, unauthenticated tour booking page.
 */
const STEP_WHEN = "when";
const STEP_WHO = "who";
const STEP_DONE = "done";

const ACCENT_TEXT_COLORS = {
  black: "var(--black-always)",
  white: "var(--white-always)",
};

function brandingStyle(branding) {
  const style = {};
  if (branding?.accent_color) style["--consult-accent"] = branding.accent_color;
  const accentText = ACCENT_TEXT_COLORS[branding?.accent_text_color];
  if (accentText) style["--consult-accent-text"] = accentText;
  return style;
}

const BookTourPage = () => {
  const { locals } = useRillContext();
  const { t } = useTranslation("book-tour");

  const config = locals?.tourConfig ?? null;
  const token = locals?.tourToken ?? "";
  const embed = Boolean(locals?.tourEmbed);
  const bootstrapError = locals?.tourError ?? null;

  const tz = config?.timezone ?? "America/New_York";
  const todayStr = React.useMemo(
    () => (config ? gymToday(tz) : ""),
    [config, tz]
  );
  const lastStr = React.useMemo(
    () => (config ? horizonEnd(tz, config.rolling_horizon_weeks) : ""),
    [config, tz]
  );

  const [displayTz, setDisplayTz] = React.useState(tz);
  React.useEffect(() => {
    setDisplayTz(tz);
  }, [tz]);
  const tzOptions = React.useMemo(() => timezoneOptionsWith(tz), [tz]);

  const [nowMs, setNowMs] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const [step, setStep] = React.useState(STEP_WHEN);
  const [selectedDay, setSelectedDay] = React.useState(null);
  const [slots, setSlots] = React.useState([]);
  const [slotsLoading, setSlotsLoading] = React.useState(false);
  const [slotsError, setSlotsError] = React.useState(null);
  const [selectedSlot, setSelectedSlot] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(null);
  const [booking, setBooking] = React.useState(null);
  const [linkDead, setLinkDead] = React.useState(false);
  const [contact, setContact] = React.useState({
    name: "",
    email: "",
    phone: "",
  });

  /**
   * Upstream messages are internal validation strings and are never localized,
   * so only the statuses with guest-facing meaning get their own copy.
   * @param {unknown} e
   * @param {string} fallbackKey
   */
  const errorMessage = React.useCallback(
    (e, fallbackKey) => {
      const status = e instanceof BookTourError ? e.status : null;
      if (status === 409) return t("errors.slotTaken");
      if (status === 429) return t("errors.tooMany");
      return t(fallbackKey);
    },
    [t]
  );

  const gymLogoUrl = config?.gym?.logo_s3_path
    ? `${S3.PATH}${config.gym.logo_s3_path}`
    : null;
  const brandStyle = React.useMemo(
    () => brandingStyle(config?.branding),
    [config?.branding]
  );

  const brandTheme = config?.branding?.theme ?? undefined;

  const dayStr = selectedDay ? ymd(selectedDay) : "";

  const loadSlots = React.useCallback(
    async (dateStr, signal) => {
      if (!dateStr) return;
      setSlotsLoading(true);
      setSlotsError(null);
      try {
        const data = await fetchTourSlots(token, dateStr, signal);
        setSlots(Array.isArray(data?.slots) ? data.slots : []);
      } catch (e) {
        if (e?.name === "AbortError") return;
        if (e instanceof BookTourError && e.status === 410) {
          setLinkDead(true);
          return;
        }
        setSlots([]);
        setSlotsError(errorMessage(e, "errors.slots"));
      } finally {
        // An aborted request has already been replaced by a newer one; clearing
        // the flag here would hide that the replacement is still loading.
        if (!signal?.aborted) setSlotsLoading(false);
      }
    },
    [token, errorMessage]
  );

  React.useEffect(() => {
    if (!dayStr) return undefined;
    const controller = new AbortController();
    setSelectedSlot(null);
    setSlots([]);
    loadSlots(dayStr, controller.signal);
    return () => controller.abort();
  }, [dayStr, loadSlots]);

  /**
   * @returns {Promise<boolean>} false when the caller should reset the captcha
   */
  const handleSubmit = React.useCallback(
    async (contact) => {
      if (!selectedSlot) return false;
      setSubmitting(true);
      setSubmitError(null);
      try {
        const created = await createTourBooking(token, {
          start_datetime: selectedSlot.start,
          end_datetime: selectedSlot.end,
          ...contact,
        });
        setBooking(created);
        setStep(STEP_DONE);
        return true;
      } catch (e) {
        if (e instanceof BookTourError && e.status === 410) {
          setLinkDead(true);
          return false;
        }
        setSubmitError(errorMessage(e, "errors.booking"));

        if (e instanceof BookTourError && e.status === 409) {
          setSelectedSlot(null);
          setStep(STEP_WHEN);
          setSlotsError(t("errors.slotTaken"));
          loadSlots(dayStr);
        }
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [selectedSlot, token, t, errorMessage, dayStr, loadSlots]
  );

  // A server-side failure is retryable; only a 410 means the link itself is dead.
  if (bootstrapError === "unavailable") {
    return (
      <main
        className={embed ? "book-tour book-tour--embed" : "book-tour"}
        style={brandStyle}
        data-theme={brandTheme}
      >
        <section className="book-tour-card book-tour-card--message">
          <h1 className="book-tour-title">{t("unavailable.title")}</h1>
          <p className="book-tour-lede">{t("unavailable.body")}</p>
          <div className="book-tour-actions">
            <button
              type="button"
              className="book-tour-btn-primary"
              onClick={() => window.location.reload()}
            >
              {t("unavailable.retry")}
            </button>
          </div>
        </section>
      </main>
    );
  }

  if (bootstrapError || !config || linkDead) {
    return (
      <main
        className={embed ? "book-tour book-tour--embed" : "book-tour"}
        style={brandStyle}
        data-theme={brandTheme}
      >
        <section className="book-tour-card book-tour-card--message">
          <h1 className="book-tour-title">{t("gone.title")}</h1>
          <p className="book-tour-lede">{t("gone.body")}</p>
        </section>
      </main>
    );
  }

  const available = slots.filter((s) => s.available);

  return (
    <main
      className={embed ? "book-tour book-tour--embed" : "book-tour"}
      style={brandStyle}
      data-theme={brandTheme}
    >
      <section className="book-tour-card">
        {(gymLogoUrl || config.gym?.name) && (
          <header className="book-tour-header">
            {gymLogoUrl && (
              <img
                className="book-tour-logo"
                src={gymLogoUrl}
                alt={config.gym?.name ?? ""}
              />
            )}
            <p className="book-tour-gym">{config.gym?.name}</p>
          </header>
        )}

        {step !== STEP_DONE && (
          <>
            <h1 className="book-tour-title">{config.name}</h1>
            {config.description && (
              <p className="book-tour-lede book-tour-description">
                {config.description}
              </p>
            )}
            <p className="book-tour-meta">
              {t("meta.duration", { minutes: config.duration_minutes })}
              {config.location_type === "online"
                ? ` · ${t("meta.online")}${
                    config.meeting_provider
                      ? ` · ${config.meeting_provider === "google_meet" ? "Google Meet" : "Zoom"}`
                      : ""
                  }`
                : config.location
                  ? ` · ${config.location}`
                  : ""}
            </p>
            <div className="book-tour-tz">
              <BookTourSelect
                id="book-tour-tz-select"
                ariaLabel={t("tz.label")}
                value={displayTz}
                onChange={setDisplayTz}
                options={tzOptions.map((opt) => ({
                  value: opt.value,
                  label: timezoneOptionLabel(opt, nowMs),
                }))}
              />
            </div>
          </>
        )}

        {step === STEP_WHEN && (
          <div className="book-tour-when">
            <div className="book-tour-calendar">
              <h2 className="book-tour-step-title">{t("steps.pickDay")}</h2>
              <DayPicker
                mode="single"
                selected={selectedDay ?? undefined}
                onSelect={(next) => setSelectedDay(next ?? null)}
                fromDate={dateFromYmd(todayStr) ?? undefined}
                toDate={dateFromYmd(lastStr) ?? undefined}
                showOutsideDays
                modifiersClassNames={{ selected: "book-tour-day-selected" }}
              />
            </div>

            <div className="book-tour-times">
              <h2 className="book-tour-step-title">
                {selectedDay ? formatCalendarDay(dayStr) : t("steps.pickTime")}
              </h2>

              {!selectedDay && (
                <p className="book-tour-hint">{t("steps.pickDayFirst")}</p>
              )}

              {selectedDay && slotsLoading && (
                <p className="book-tour-hint">{t("steps.loading")}</p>
              )}

              {selectedDay && slotsError && (
                <p className="book-tour-error" role="alert">
                  {slotsError}
                </p>
              )}

              {selectedDay &&
                !slotsLoading &&
                !slotsError &&
                (available.length === 0 ? (
                  <p className="book-tour-hint">{t("steps.noTimes")}</p>
                ) : (
                  <ul className="book-tour-slot-list">
                    {available.map((slot) => {
                      const active = selectedSlot?.start === slot.start;
                      return (
                        <li key={slot.start}>
                          <button
                            type="button"
                            className={
                              active
                                ? "book-tour-slot is-selected"
                                : "book-tour-slot"
                            }
                            aria-pressed={active}
                            onClick={() => setSelectedSlot(slot)}
                          >
                            {formatSlotTime(slot.start, displayTz)}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ))}

              {selectedSlot && (
                <div className="book-tour-actions">
                  <button
                    type="button"
                    className="book-tour-btn-primary"
                    onClick={() => {
                      setSubmitError(null);
                      setStep(STEP_WHO);
                    }}
                  >
                    {t("steps.continue")}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {step === STEP_WHO && selectedSlot && (
          <div className="book-tour-who">
            <h2 className="book-tour-step-title">{t("steps.yourDetails")}</h2>
            <p className="book-tour-chosen">
              {formatLongDate(selectedSlot.start, displayTz)} ·{" "}
              {formatSlotTime(selectedSlot.start, displayTz)} (
              {zoneLabel(selectedSlot.start, displayTz)})
            </p>
            <BookTourForm
              values={contact}
              onValuesChange={setContact}
              submitting={submitting}
              submitError={submitError}
              onBack={() => setStep(STEP_WHEN)}
              onSubmit={handleSubmit}
            />
          </div>
        )}

        {step === STEP_DONE && booking && (
          <BookTourConfirmation
            booking={booking}
            config={config}
            displayTz={displayTz}
            onBookAnother={() => {
              setBooking(null);
              setSelectedSlot(null);
              setStep(STEP_WHEN);
              if (dayStr) loadSlots(dayStr);
            }}
          />
        )}
      </section>

      {!embed && (
        <footer className="book-tour-footer">
          {t("footer.poweredBy")}{" "}
          <a href="https://mystrengthbook.com" rel="noopener noreferrer">
            MyStrengthBook
          </a>
        </footer>
      )}
    </main>
  );
};

export default BookTourPage;
